<template>
  <v-dialog
    v-model="displayFilter"
    class="filter-content"
    max-width="660"
  >
    <template v-slot:activator="{ on }">
      <v-badge
        color="reds"
        avatar
        bordered
        dot
        overlap
        :value="totalAppliedDataTools"
      >
        <template
          v-slot:badge
        >
          {{ totalAppliedDataTools }}
        </template>

        <v-btn
          small
          outlined
          v-on="on"
        >
          <font-awesome-icon
            :icon="['fas', 'filter']"
            size="sm"
          />

          <span class="pl-1">Filter</span>
        </v-btn>
      </v-badge>
    </template>

    <v-card class="pb-12">
      <v-card-title class="title pl-10 py-6">
        <v-row>
          <v-col
            :class="`flex-grow py-0 ${inDarkMode ? 'white--text' : 'primary--text'}`"
          >
            Tools
          </v-col>

          <v-spacer />

          <v-col
            class="flex-shrink-1 col-2 py-0 text-right"
          >
            <v-btn
              :ripple="false"
              icon
              @click.stop="displayFilter = false"
            >
              <v-icon color="brightblue">
                close
              </v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider />

      <v-card-text class="px-6 pb-6">
        <slot name="filter-controls" />

        <v-row
          align="center"
          justify="end"
          class="pl-6 pr-6 pt-6"
        >
          <v-spacer />

          <v-btn
            text
            small
            color="error"
            class="text-none"
            :loading="resettingFilter"
            @click="onReset(true)"
          >
            Reset
            <template v-slot:loader>
              <span class="custom-loader">
                <v-progress-circular
                  value="25"
                  size="20"
                />
              </span>
            </template>
          </v-btn>

          <v-btn
            small
            primary
            dark
            color="brightblue"
            class="elevation-0"
            :loading="applyingFilter"
            @click="onApply(true)"
          >
            Apply
            <template v-slot:loader>
              <span class="custom-loader">
                <v-progress-circular
                  value="25"
                  size="20"
                />
              </span>
            </template>
          </v-btn>
        </v-row>

        <slot name="saved-filters" />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import dates from 'src/utils/dates'
import render from 'src/utils/render'

const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapGetters, mapState } = createNamespacedHelpers('tools')
const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')

export default {
  name: 'FilterForm',
  data() {
    return {
      showAdvancedTools: false,
    }
  },
  computed: {
    ...mapAuthGetters(['inDarkMode']),
    displayFilter: {
      get() {
        return this.$store.state.tools.displayFilter
      },
      set(value) {
        this.$store.dispatch('tools/displayFilter', value)
      },
    },
    displayAdvancedTools: {
      get() {
        return this.$store.state.tools.displayAdvancedTools
      },
      set(value) {
        this.$store.dispatch('tools/toggleAdvancedTools', value)
      },
    },
    ...mapLayoutGetters(['showFilterBar']),
    ...mapGetters(['currentItems', 'currentSummary', 'reportKey', 'totalAppliedDataTools']),
    ...mapState({
      applyingFilter: (state) => state.applyingFilter,
      currentReportParams: (state) => state.currentReportParams,
      filterData: (state) => state.filterData,
      items: (state) => state.items,
      reportId: (state) => state.reportId,
      reportName: (state) => state.reportName,
      resettingFilter: (state) => state.resettingFilter,
      subId: (state) => state.subId,
    }),
  },
  watch: {
    displayFilter(newValue) {
      this.$emit('on-toggle-filters', newValue)
    },
  },
  methods: {
    toggleAdvancedTools() {
      this.displayAdvancedTools = !this.displayAdvancedTools
    },
    onReset(emitEvent = false) {
      render.ensureViewIsUpdatedAndRenderedImmediately(async () => {
        await this.$store.commit('tools/resettingFilter', true)

        if (emitEvent) {
          const { currentReportParams, reportKey } = this
          const getterMap = {
            'network-health': () => ({ dateRange: [dates.get('1970-01-01'), dates.get('2200-01-01')] }),
            'adx-health': () => ({ dateRange: [dates.get('1970-01-01'), dates.get('2200-01-01')] }),
            'adx-and-ob': () => ({ dateRange: [dates.get('1970-01-01'), dates.get('2200-01-01')] }),
            time: () => ({ dateRange: dates.mtdFormatted() }),
          }

          const apiParams = typeof getterMap[reportKey] === 'function' ? getterMap[reportKey]() : {}

          // Update date range on state so tools/setView gets the correct API params.
          this.$store.commit('tools/currentReportParams', { ...currentReportParams, ...apiParams })

          await this.$store.dispatch('tools/resetFilters')

          this.$store.dispatch('tools/displayFilter', false)
        }

        this.$store.commit('tools/resettingFilter', false)
      })
    },
    onApply(emitEvent = false) {
      render.ensureViewIsUpdatedAndRenderedImmediately(async () => {
        await this.$store.commit('tools/applyingFilter', true)

        if (emitEvent) {
          const { currentItems, currentReportParams, filterData, reportId, reportKey, reportName, subId } = this
          const makeDateRangeUsingOperator = (operator, dateRange) => {
            const valueMap = {
              range: () => [dateRange[0] || dates.get('1970-01-01'), dateRange[1] || dates.get('2200-01-01')],
              eq: () => [dateRange, dateRange],
              neq: () => [dates.get('1970-01-01'), dates.get('2200-01-01')], // The filter will exclude the neq value.
              gte: () => [dateRange, dates.get('2200-01-01')],
              lte: () => [dates.get('1970-01-01'), dateRange],
            }

            return { dateRange: valueMap[operator]() }
          }
          const dateRangeGetterForTimeReports = () => {
            const { timeEntryDate: { operator, value: [dateRange] } } = filterData[reportKey]
            return makeDateRangeUsingOperator(operator, dateRange)
          }
          const dateRangeGetterForDateReports = () => {
            const { date: { operator, value: [dateRange] } } = filterData[reportKey]
            return makeDateRangeUsingOperator(operator, dateRange)
          }

          // Only time and programmatic routes need to update the date range,
          // as they are the only routes that hit the API with a date range.
          const getterMap = {
            'network-health': dateRangeGetterForDateReports,
            'adx-health': dateRangeGetterForDateReports,
            'adx-and-ob': dateRangeGetterForDateReports,
            time: dateRangeGetterForTimeReports,
          }

          const apiParams = typeof getterMap[reportKey] === 'function' ? getterMap[reportKey]() : {}

          // Determine the items we'll filter and the params we'll need to filter them.
          const report = {
            // Action that triggered the filter.
            action: 'Submit Filters',

            // These are the params we'll need to pass to the API, if needed.
            ...apiParams,

            // If dateRange is set on time or programmatic routes, `items` will be updated later.
            items: currentItems,

            // These are the params we need to filter the items.
            reportKey,
            reportId,
            reportName,
            subId,
          }

          // Update date range on state so tools/setView gets the correct API params.
          this.$store.commit('tools/currentReportParams', { ...currentReportParams, ...apiParams })

          await this.$store.dispatch('tools/applyFilters', report)

          this.$store.dispatch('tools/displayFilter', false)

          // const dateRangeQueryString = this.$route.query.dateRange || ''
          // const usesDash = /-/.test(dateRangeQueryString)
          //
          // const dateRangeString = dateRange ? dateRange.join(usesDash ? '-' : ',') : ''
          //
          // if (dateRangeQueryString.length > 0 && dateRangeQueryString !== dateRangeString) {
          //   // Update the URL's query string.
          //   this.$router.push({ query: { ...this.$route.query, dateRange: dateRangeString } })
          // }
        }

        this.$store.commit('tools/applyingFilter', false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.filter {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: 100%;
}

.filter-content {
  background: $color-monochrome-white;
  border-radius: 6px;
  width: 100%;
}

.controls-container {
  background: $color-monochrome-white;
  padding: 24px;
  display: flex;

  @media screen and (max-width: $vuetify-md-max-size) {
    flex-direction: column;

    .filter-label {
      padding-top: 0;
      padding-bottom: 10px;
    }
  }
}

.filter-label {
  text-transform: uppercase;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.21px;
  line-height: 11px;
}

.controls {
  flex: 2;
  position: relative;
}

</style>
